import * as React from "react";
import Button from "app/components/Button";
import { formatMoney, formatMoneyNoCurrency, isFiat } from "app/lib/money";
import { COIN_IMG_BASE_URL, PAYMENT_LINK, PAYMENT_REQUEST } from "app/constants/variables";
import Text from "app/styles/Text";
import {
  AppStateActions,
  useAppDispatch,
  useAppState,
} from "app/providers/AppStateProvider";
import useGetBalances from "../hooks/useGetBalances";
import LoaderContainer from "app/components/LoaderContainer";
import useListCurrencies from "../hooks/useListCurrencies";
import BalancesMenu from "./BalancesMenu";
import { BalanceTypeEnum } from "app/models/balance";
import tw from "twin.macro";
import useCreatePayment from "../hooks/useCreatePayment";
import usePayPaymentLink from "../hooks/usePayPaymentLink";
import { CreatePaymentResponse } from "app/api/payment/types";
import useFinalizeRequest from "../hooks/useFinalizeRequest";
import { PAYMENT_PAGE, TRANSFER_ROOT } from "app/constants/routes";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import InsufficientBalanceModal from "./InsufficientBalanceModal";
import { useGetPairs } from "../hooks/useGetPairs";


export default function SummaryView({ onDone }: { onDone: (options?: { skipVerify?: boolean }) => void }) {
  // const navigate = useNavigate();
  const { bushaAuthToken, merchant, source, sourceId, userPayload, payment } = useAppState();
  const isPaymentRequest = source === PAYMENT_REQUEST || !source;

  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const {
    data: _balances,
    error: balancesError,
    isLoading: isLoadingBalances,
    refetch: refetchBalances,
  } = useGetBalances(bushaAuthToken ?? "");
  const balances = isPaymentRequest ? _balances?.filter((b) => b.type !== BalanceTypeEnum.fiat) : _balances;

  const { createPayment, isLoading: isLoadingCreatePayment } = useCreatePayment();
  const { createPaymentLinkPayment, isLoading: isLoadingCreatePaymentLinkPayment } =
    usePayPaymentLink();

  const {
    currencies,
    isLoading: isLoadingCurrencies,
    error: currenciesError,
    refetch: refetchCurrencies,
  } = useListCurrencies({
    type: "crypto",
    payments_request: true,
    publicKey: merchant?.public_key,
  });

  const {
    data: pairs,
    isLoading: isLoadingPairs,
    error: pairsError,
    refetch: refetchPairs,
  } = useGetPairs(bushaAuthToken ?? "", { publicKey: merchant?.public_key ?? "", counter: merchant?.target_currency ?? "" });

  const supportedCurrencies = React.useMemo(() => balances?.filter((b) =>
    b.type === BalanceTypeEnum.fiat ? true :
      Boolean(currencies
        ?.find((c) => c.supported_networks.length > 0 && c.code === b.currency))
      // usdt - usdt is not supported by pairs
      && (merchant?.target_currency === "USDT" ? true : pairs?.some((p) => p.base === b.currency))
  ), [balances, currencies, pairs, merchant?.target_currency]);

  const fee = 0 as const;

  const [isInsufficientModalVisible, setIsInsufficientModalVisible] = React.useState(false);
  const [selectedCurrency, setSelectedCurrency] = React.useState<string | null>(() => payment?.source_currency ?? null);

  const [paymentRequest, setPaymentRequest] = React.useState<CreatePaymentResponse>();

  const selectedAsset = supportedCurrencies?.find(
    (a) => a.currency === selectedCurrency
  );

  const details = {
    "Amount to pay": `${formatMoney(+merchant.quote_amount!, "crypto")} ${merchant.quote_currency
      }`,
    [`Amount in ${selectedCurrency}`]: paymentRequest ? `${formatMoney(
      +paymentRequest?.source_amount!,
      selectedAsset?.type
    )} ${paymentRequest?.source_currency}` : "-",
    Rate: paymentRequest?.quote_rate?.rate_explained,
    Fee: `${formatMoney(fee, selectedAsset?.type)} ${selectedCurrency}`,
    Total: `${formatMoney(
      +(fee ?? 0) + +(paymentRequest?.source_amount ?? 0),
      selectedAsset?.type
    )} ${selectedCurrency}`,
  };

  const handlePay = React.useCallback((selectedCurrency: string) => {
    const isPaymentRequest = source === PAYMENT_REQUEST;
    const isPaymentLink = source === PAYMENT_LINK;
    if (isPaymentRequest || !source) {
      createPayment({
        additional_info: merchant?.meta || {},
        quote_currency: merchant?.quote_currency || "",
        quote_amount: merchant?.quote_amount,
        source_currency: selectedCurrency!,
        target_currency: merchant?.target_currency!,
        token: bushaAuthToken,
        publicKey: merchant?.public_key,
        reference: merchant?.reference ?? undefined,
      }, {
        onSettled(data) {
          setPaymentRequest(data?.data.data);
          if (data && !data?.data.data.two_step) {
            dispatch({
              type: AppStateActions.setPayment,
              payload: data.data.data,
            });
          }
        },
        onError(error) {
          if (axios.isAxiosError(error)) {
            if (error.response?.data.error.message.toLowerCase().includes("insufficient balance")) {
              setIsInsufficientModalVisible(true);
            }
          }
        },
      });
    } else if (sourceId && isPaymentLink) {
      createPaymentLinkPayment({
        paymentId: sourceId,
        source_currency: selectedCurrency!,
        target_currency: merchant?.target_currency || "",
        quote_currency: merchant?.quote_currency || "",
        quote_amount: merchant?.quote_amount,
        token: bushaAuthToken,
        requested_info: merchant?.meta || {
          email: userPayload?.email!,
          firstname: userPayload?.firstname,
          lastname: userPayload?.lastname,
        },
        reference: merchant?.reference ?? undefined,
      }, {
        onSettled(data) {
          setPaymentRequest(data?.data.data);
          if (data && !data?.data.data.two_step) {
            dispatch({
              type: AppStateActions.setPayment,
              payload: data.data.data,
            });
          }
        },
        onError(error) {
          if (axios.isAxiosError(error)) {
            if (error.response?.data.error.message.toLowerCase().includes("insufficient balance")) {
              setIsInsufficientModalVisible(true);
            }
          }
        },
      });
    }
  }, [
    createPayment,
    source,
    sourceId,
    bushaAuthToken,
    merchant,
    userPayload,
    createPaymentLinkPayment,
    dispatch,
  ]);

  const { finalizePaymentRequest, isLoading: isLoadingFinalizeRequest } = useFinalizeRequest();

  const handleFinalizeRequest = React.useCallback(() => {
    if (!paymentRequest) return;
    finalizePaymentRequest({
      token: bushaAuthToken!,
      paymentRequestId: paymentRequest?.id,
    }, {
      onSuccess(data) {
        dispatch({
          type: AppStateActions.setPayment,
          payload: data.data.data,
        });
        onDone({ skipVerify: true });
      },
    });
  }, [paymentRequest, bushaAuthToken, finalizePaymentRequest, dispatch, onDone]);

  React.useEffect(() => {
    if (!selectedCurrency) return;
    handlePay(selectedCurrency);
  }, [selectedCurrency, handlePay]);

  React.useEffect(() => {
    if (supportedCurrencies?.length && !selectedCurrency) {
      setSelectedCurrency(supportedCurrencies.find((c) => +c.available.amount > 0)?.currency ?? supportedCurrencies[0].currency);
    }
  }, [supportedCurrencies, selectedCurrency]);

  const selectedBalance = balances?.find((b) => b.currency === selectedCurrency);

  const isLoading = isLoadingBalances || isLoadingCurrencies || isLoadingPairs || ((isLoadingCreatePayment || isLoadingCreatePaymentLinkPayment) && !paymentRequest);

  const refetch = () => {
    if (balancesError) {
      refetchBalances();
    }
    if (currenciesError) {
      refetchCurrencies();
    }
    if (pairsError) {
      refetchPairs();
    }
  }

  return (
    <>
      <LoaderContainer
        loading={isLoading}
        onRetry={refetch}
        error={!!balancesError || !!currenciesError || !!pairsError}
        customLoader={shimmer}
        css={[isInsufficientModalVisible ? tw`opacity-0` : tw`opacity-100`, tw`justify-start`]}
      >
        <div tw="flex flex-col gap-[24px] grow overflow-hidden">
          <div tw="flex flex-col gap-[8px] grow overflow-y-auto">
            <div tw="flex flex-col gap-[8px]">
              <Text css={tw`title-medium text-action-primary`}>Pay with</Text>

              <BalancesMenu
                supportedCurrencies={supportedCurrencies?.map((b) => ({
                  name: b.currency,
                  code: b.currency,
                  description: formatMoneyNoCurrency(+(b.available.amount ?? 0), isFiat(b.available.currency ?? "") ? "fiat" : "crypto") + " " + b.available.currency,
                }))}
                handleCurrencyChange={setSelectedCurrency}
                value={selectedCurrency ?? ""}
              >
                <div tw="flex items-center gap-[16px] bg-containment-tertiary rounded-[16px] p-[16px] w-full">
                  <img
                    src={`${COIN_IMG_BASE_URL}/${selectedCurrency}`}
                    alt={selectedCurrency ?? ""}
                    tw="w-[40px] h-[40px] rounded-full"
                  />
                  <div tw="flex flex-col items-start grow">
                    <Text css={tw`body-emphasized`}>{selectedCurrency}</Text>
                    <Text css={tw`body-small text-text-mid`}>
                      {formatMoneyNoCurrency(+(selectedBalance?.available.amount ?? 0), isFiat(selectedBalance?.available.currency ?? "") ? "fiat" : "crypto")} {selectedBalance?.available.currency}
                    </Text>
                  </div>

                  <span css={tw`bg-action-secondary py-s1 px-s2 rounded-[16px] label-large text-text-on-secondary`}>
                    Change
                  </span>
                </div>
              </BalancesMenu>
            </div>

            <ul tw="bg-containment-tertiary flex flex-col rounded-[16px] gap-[20px] p-[16px]">
              {Object.keys(details).map((item) => (
                <li key={item} tw="flex items-center justify-between">
                  <p css={tw`body-medium text-text-mid`}>{item}</p>

                  <p css={tw`label-xxl`}>
                    {details[item as keyof typeof details]}
                  </p>
                </li>
              ))}
            </ul>

            <Text subdued css={tw`text-center body-small`}>
              By confirming, you allow Busha to charge your wallet according to the merchant's terms.
            </Text>
          </div>

          <Button
            fullWidth
            size="xBig"
            variant="primary"
            disabled={isLoadingCurrencies || !paymentRequest || isLoadingCreatePayment || isLoadingCreatePaymentLinkPayment}
            loading={isLoadingFinalizeRequest}
            tw="mt-auto flex-shrink-0"
            onClick={() => {
              if (selectedBalance && paymentRequest && +selectedBalance?.available.amount < +paymentRequest?.source_amount) {
                setIsInsufficientModalVisible(true);
                return;
              }

              if (!paymentRequest) return;

              if (paymentRequest.two_step) {
                dispatch({
                  type: AppStateActions.setPayment,
                  payload: paymentRequest,
                });
                onDone();
              } else {
                handleFinalizeRequest();
              }
            }}
          >
            Pay {formatMoneyNoCurrency(+(merchant?.quote_amount ?? 0))} {merchant?.quote_currency}
          </Button>
        </div>
      </LoaderContainer>

      <InsufficientBalanceModal
        isOpen={isInsufficientModalVisible}
        onClose={() => setIsInsufficientModalVisible(false)}
        onPayWithCryptoWallet={() => navigate(`${TRANSFER_ROOT}/${PAYMENT_PAGE}`)}
        currency={selectedCurrency ?? ""}
      />
    </>
  );
}

const shimmer = (
  <div tw="flex flex-col gap-[24px] w-full h-full">
    <div tw="flex flex-col gap-[8px]">
      <div tw="h-[16px] w-[98px] rounded-[12px] bg-containment-tertiary" />

      <div tw="flex items-center gap-[16px] bg-input-surface rounded-[16px] p-[16px]">
        <div tw="w-[40px] h-[40px] rounded-full bg-containment-primary animate-pulse" />
        <div tw="grow">
          <div tw="animate-pulse h-[16px] w-[98px] rounded-[999px] bg-containment-primary mb-[4px]"></div>
          <div tw="animate-pulse h-[24px] w-[179px] rounded-[999px] bg-containment-primary"></div>
        </div>
        <div tw="w-[30px] h-[30px] rounded-full bg-containment-primary animate-pulse" />
      </div>
    </div>

    <div tw="flex flex-col gap-[20px] bg-containment-tertiary rounded-[16px] p-[16px]">
      {Array.from({ length: 4 }).fill(0).map((_, index) => (
        <div key={index} tw="flex items-center justify-between">
          <div tw="animate-pulse h-[20px] w-[168px] rounded-[999px] bg-containment-primary"></div>
          <div tw="animate-pulse h-[20px] w-[124px] rounded-[999px] bg-containment-primary"></div>
        </div>
      ))}
    </div>

    <div tw="h-[56px] w-full rounded-[999px] bg-containment-secondary mt-auto" />
  </div>
)